import { useEffect } from "react";

import { useSearchParams, useLocation } from "react-router-dom";

import Button from "@mui/material/Button";
import styled from "@mui/material/styles/styled";

import useConnectCalendar from "@app/analytics/hooks/calendar/useConnectCalendar";
import useConnectedProviders from "@app/analytics/hooks/sso/useConnectedProviders";
import useAuth from "@app/common/auth/useAuth";
import { CALENDAR_PROVIDERS } from "@app/common/constants/providers";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import useLocalStorage from "@app/common/hooks/useLocalStorage";
import { toast } from "@app/common/hooks/useToasts";
import ROUTES from "@app/common/routes";
import type { CalendarProviderId } from "@app/common/types/providerTypes";

import AlertBanner from "@rui/AlertBanner";
import Stack, { Row } from "@rui/Stack";
import { Bold } from "@rui/styles";

const TRUNCATED_PROVIDER_LABEL: { [key in CalendarProviderId]: string } = {
  google_calendar: "Google",
  microsoft_graph_calendar: "Outlook",
  zoom_calendar: "Zoom",
};

const ProviderButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#FFF",
  borderColor: theme.palette.shades.primary[500],
  height: "40px",
  color: theme.palette.shades.neutral[800],
}));

const Content = styled(Stack)(({ theme }) => ({
  flexDirection: "column",
  [theme.breakpoints.up("md")]: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
}));

const CalendarBanner = () => {
  const [queryParams] = useSearchParams();
  const location = useLocation();
  const [showCalendarBanner, setShowCalendarBanner] = useLocalStorage<
    boolean | null
  >("showCalendarBanner", null, { migrateKeys: true });

  const { hasConnectedCalendarProviders, isLoading } = useConnectedProviders();
  const { connectCalendar } = useConnectCalendar();

  const calendarConnectSuccess =
    queryParams.get("action") === "calendarSuccess" &&
    queryParams.get("success") === "true";

  const calendarConnectError =
    queryParams.get("action") === "calendarError" &&
    queryParams.get("error") === "true";

  const shouldShowCalendarBanner =
    !isLoading && !hasConnectedCalendarProviders && showCalendarBanner != false;

  useEffect(() => {
    // the user settings page already has its own handling for a calendar connect response
    if (location.pathname.includes(ROUTES.USER_SETTINGS_ROOT)) {
      return;
    }

    if (calendarConnectSuccess) {
      toast.success("Successfully connected calendar!");
    } else if (calendarConnectError) {
      toast.error(
        "There was an problem connecting your calendar. Please try again later."
      );
    }
  }, [calendarConnectError, calendarConnectSuccess]);

  if (!shouldShowCalendarBanner) return null;

  const handleDismiss = () => setShowCalendarBanner(false);

  return (
    <AlertBanner
      severity="info"
      onClose={handleDismiss}
      contentVariant="custom"
    >
      <Content spacing={2}>
        <span>
          <Bold>You haven't connected your calendar!</Bold> Without calendar
          integration, you won’t see upcoming meetings or reports associated
          with calendar events. Select your calendar provider to continue.
        </span>
        <Row spacing={1}>
          {CALENDAR_PROVIDERS.map((provider) => {
            const label = TRUNCATED_PROVIDER_LABEL[provider.id];
            return (
              <ProviderButton
                startIcon={
                  <img
                    src={provider.icon}
                    height="24"
                    alt={`Connect with ${label}`}
                  />
                }
                key={provider.id}
                variant="outlined"
                onClick={() => connectCalendar(provider.id)}
              >
                {label}
              </ProviderButton>
            );
          })}
        </Row>
      </Content>
    </AlertBanner>
  );
};

const CalendarBannerGate = () => {
  const { user } = useAuth();
  const { canConnectCalendars } = useFeatureAccess();

  if (!user || !canConnectCalendars) {
    return null;
  }

  return <CalendarBanner />;
};

export default CalendarBannerGate;
